import { motion } from "framer-motion";
import { ArrowUpRight, AlertCircle } from "lucide-react";
import Field from "./Field";
import LocationFields from "./LocationFields";
import ServicesField from "./ServicesField";
import { PROJECT_TYPES, getBudgetTiers } from "../../../data/formOptions";

const TIMELINES = [
    "Planning",
    "Pre-construction",
    "Under construction",
    "Retrofit",
    "Exploratory",
];

const inputCls =
    "w-full bg-transparent border-b border-[rgba(201,168,106,0.3)] focus:border-[#C9A86A] outline-none text-[#F3EFE7] text-base py-2.5 px-1 transition-colors duration-300 placeholder:text-[#5e5a52]";

const selectCls =
    "w-full bg-[#0B0B0C] border-b border-[rgba(201,168,106,0.3)] focus:border-[#C9A86A] outline-none text-[#F3EFE7] text-base py-2.5 px-1 transition-colors duration-300 appearance-none";

export default function InquiryFields({
    form,
    errors,
    status,
    submitError,
    setField,
    onSubmit,
}) {
    return (
        <motion.form
            key="form"
            initial={false}
            onSubmit={onSubmit}
            noValidate
            className="space-y-7"
            data-testid="inquiry-form"
        >
            {/* Honeypot */}
            <input
                type="text"
                name="website"
                tabIndex="-1"
                autoComplete="off"
                value={form.website}
                onChange={(e) => setField("website", e.target.value)}
                aria-hidden="true"
                style={{
                    position: "absolute",
                    left: "-9999px",
                    width: 1,
                    height: 1,
                    opacity: 0,
                }}
                data-testid="inquiry-honeypot"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                <Field
                    label="Full Name"
                    htmlFor="i_full_name"
                    required
                    error={errors.full_name}
                >
                    <input
                        id="i_full_name"
                        type="text"
                        autoComplete="name"
                        value={form.full_name}
                        onChange={(e) =>
                            setField("full_name", e.target.value)
                        }
                        className={inputCls}
                        data-testid="inquiry-full_name"
                    />
                </Field>
                <Field
                    label="Email"
                    htmlFor="i_email"
                    required
                    error={errors.email}
                >
                    <input
                        id="i_email"
                        type="email"
                        autoComplete="email"
                        value={form.email}
                        onChange={(e) => setField("email", e.target.value)}
                        className={inputCls}
                        data-testid="inquiry-email"
                    />
                </Field>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                <Field label="Phone" htmlFor="i_phone" hint="Optional">
                    <input
                        id="i_phone"
                        type="tel"
                        autoComplete="tel"
                        value={form.phone}
                        onChange={(e) => setField("phone", e.target.value)}
                        className={inputCls}
                        data-testid="inquiry-phone"
                    />
                </Field>
                <Field
                    label="Project Type"
                    htmlFor="i_project_type"
                    required
                    error={errors.project_type}
                >
                    <select
                        id="i_project_type"
                        value={form.project_type}
                        onChange={(e) =>
                            setField("project_type", e.target.value)
                        }
                        className={selectCls}
                        data-testid="inquiry-project_type"
                    >
                        <option value="">Select…</option>
                        {PROJECT_TYPES.map((p) => (
                            <option key={p.value} value={p.value}>
                                {p.label}
                            </option>
                        ))}
                    </select>
                </Field>
            </div>

            <LocationFields
                form={form}
                errors={errors}
                setField={setField}
                selectCls={selectCls}
                inputCls={inputCls}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                <Field
                    label="Budget Range"
                    htmlFor="i_budget"
                    hint="Optional"
                >
                    <select
                        id="i_budget"
                        value={form.budget}
                        onChange={(e) => setField("budget", e.target.value)}
                        className={selectCls}
                        data-testid="inquiry-budget"
                    >
                        <option value="">Select…</option>
                        {getBudgetTiers(form.project_type).map((b) => (
                            <option key={b.value} value={b.value}>
                                {b.label}
                            </option>
                        ))}
                    </select>
                </Field>
                <Field
                    label="Timeline"
                    htmlFor="i_timeline"
                    required
                    error={errors.timeline}
                >
                    <select
                        id="i_timeline"
                        value={form.timeline}
                        onChange={(e) =>
                            setField("timeline", e.target.value)
                        }
                        className={selectCls}
                        data-testid="inquiry-timeline"
                    >
                        <option value="">Select…</option>
                        {TIMELINES.map((t) => (
                            <option key={t} value={t}>
                                {t}
                            </option>
                        ))}
                    </select>
                </Field>
            </div>

            <ServicesField form={form} errors={errors} setField={setField} />

            <Field
                label="Tell us about the project"
                htmlFor="i_message"
                required
                error={errors.message}
            >
                <textarea
                    id="i_message"
                    rows={5}
                    value={form.message}
                    onChange={(e) => setField("message", e.target.value)}
                    className={`${inputCls} resize-y`}
                    data-testid="inquiry-message"
                />
            </Field>

            <div>
                <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                        type="checkbox"
                        checked={form.consent}
                        onChange={(e) =>
                            setField("consent", e.target.checked)
                        }
                        className="mt-1 w-4 h-4 accent-[#C9A86A] cursor-pointer"
                        aria-describedby={
                            errors.consent ? "i_consent-error" : undefined
                        }
                        data-testid="inquiry-consent"
                    />
                    <span className="text-[#9A958A] text-sm leading-[1.7]">
                        I agree to be contacted about my inquiry.
                    </span>
                </label>
                {errors.consent && (
                    <p
                        id="i_consent-error"
                        role="alert"
                        className="mt-2 flex items-center gap-2 text-[12px] text-[#C97A6A]"
                    >
                        <AlertCircle size={12} strokeWidth={1.6} />
                        {errors.consent}
                    </p>
                )}
            </div>

            {submitError && (
                <div
                    role="alert"
                    className="border border-[#C97A6A]/40 bg-[#C97A6A]/5 px-4 py-3 text-[13px] text-[#E0A89A] leading-[1.7]"
                    data-testid="inquiry-submit-error"
                >
                    {submitError}
                </div>
            )}

            <div className="pt-2">
                <button
                    type="submit"
                    disabled={status === "loading"}
                    className="btn-gold w-full md:w-auto disabled:opacity-70 disabled:cursor-wait"
                    data-testid="inquiry-submit"
                >
                    {status === "loading" ? (
                        <>
                            <span className="inline-block w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                            Sending
                        </>
                    ) : (
                        <>
                            Request Consultation
                            <ArrowUpRight size={14} strokeWidth={1.4} />
                        </>
                    )}
                </button>
            </div>
        </motion.form>
    );
}
